<template>
  <div class="checkoutPanel">

    <div class="checkoutPanel__products scroll-container">
      <div v-for="item in selectedItems" :key="item.id">
        <router-link :to="{name: 'ProductCardView', query: {priceListItemId: item.price_list_item_id }}">
          <img
              class="checkoutPanel__image"
              :src="item.img_url"
              alt=""
              @click.stop.prevent="openImagePreview(item.img_url)"
              @error="(e)=>e.target.src=require('@/assets/img/preview_placeholder.jpg')"

          />
        </router-link>
      </div>
    </div>

    <div class="checkoutPanel__options" v-if="canUseDelivery">
      <label>Способ получения</label>
      <div class="checkoutPanel__deliveryToggle">
        <button
            :class="['checkoutPanel__toggleBtn', { 'checkoutPanel__toggleBtn--active': !useDelivery }]"
            @click="useDelivery = false"
        >Самовывоз</button>
        <button
            :class="['checkoutPanel__toggleBtn', { 'checkoutPanel__toggleBtn--active': useDelivery }]"
            @click="useDelivery = true"
        > Доставка ПВЗ</button>
      </div>
    </div>

    <div class="checkoutPanel__options" v-if="canUseDelivery && useDelivery">
      <label>Пункт выдачи</label>
      <form-input :model-value="calcPoint" disabled/>
    </div>

    <div class="checkoutPanel__costRow" v-if="useDelivery && deliveryCost">
      <span class="checkoutPanel__costLabel">Стоимость доставки</span>
      <span class="checkoutPanel__costValue">{{ deliveryCost }} ₽</span>
    </div>

    <div class="checkoutPanel__options">
      <label class="checkoutPanel__textLabel">Промокод</label>
      <div class="checkoutPanel__promoRow">
        <form-input
            v-model="promoCodeInput"
            :disabled="!!appliedPromo || promoBusy"
            placeholder="Введите код"
        />
        <ActionButton
            v-if="!appliedPromo"
            :disabled="promoBusy"
            @button-click="applyPromoHandler"
        >
          <template #caption>Применить</template>
        </ActionButton>
        <ActionButton
            v-else
            @button-click="removePromoHandler"
        >
          <template #caption>Убрать</template>
        </ActionButton>
      </div>
      <div class="checkoutPanel__promoHint" v-if="appliedPromo">
        Промокод {{ appliedPromo.code }}: скидка {{ promoDiscount }} ₽
      </div>
    </div>

    <div class="checkoutPanel__costRow" v-if="promoDiscount > 0">
      <span class="checkoutPanel__costLabel">Скидка по промокоду</span>
      <span class="checkoutPanel__costValue">−{{ promoDiscount }} ₽</span>
    </div>

    <div class="checkoutPanel__footer">
      <div class="checkoutPanel__footerTotal">
        Итого
        <div class="checkoutPanel__footerTotalSum">
          {{ totalSum }} ₽
        </div>
      </div>
      <ActionButton
          @button-click="checkoutHandler"
          style="background-color: #6FC062; color: #212226;width: 160px"
      >
        <template #caption>Оформить заказ</template>
      </ActionButton>
<!--      <button @click="handlePayment">Оплатить</button>-->
    </div>

  </div>
</template>

<script>
import FormInput from "@/components/ui/FormInput.vue";
import {computed, ref, watch} from "vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import {useCartStore} from "@/store/cart";
import {useOrdersStore} from "@/store/orders";
import {useToast} from "vue-toast-notification";
import {usePayment} from "@/composables/payment";
import {useSettingsStore} from "@/store/settings";
import {checkEmail, checkPhone} from "@/tools/validation.tools";
import {openImagePreview} from "@/composables/image-preview";


export default {
  name: "CheckoutPanel",
  components: {ActionButton, FormInput},
  props: ['selectedPoint'],
  emits: ['update:showMap'],
  setup(props, { emit }) {
    const settings = useSettingsStore();
    const promoCodeInput = ref('')
    const promoBusy = ref(false)
    const cartStore = useCartStore()
    const ordersStore = useOrdersStore()
    const toast = useToast();
    const payment = usePayment();
    const busy = ref(false)
    const useDelivery = ref(false)
    let pendingPromoCode = '';
    let promoRefreshTimer = null;
    let promoRefreshGeneration = 0;

    function cancelPromoRefresh() {
      pendingPromoCode = '';
      promoRefreshGeneration++;
      if (promoRefreshTimer) {
        clearTimeout(promoRefreshTimer);
        promoRefreshTimer = null;
      }
    }

    const items = computed(() => {
      return cartStore.items;
    })

    const selectedItems = computed(() => {
      return items.value.filter(item => cartStore.selectedIds.includes(item.id));
    });

    const canUseDelivery = computed(() => {
      return selectedItems.value.some(item => item.supports_delivery === true);
    });

    watch(
      () => canUseDelivery.value && useDelivery.value,
      (val) => emit('update:showMap', val),
      { immediate: true }
    );

    const deliveryCost = computed(() => {
      return settings.deliveryCost || null;
    });

    const appliedPromo = computed(() => ordersStore.promo);
    const promoDiscount = computed(() => appliedPromo.value?.discount_total || 0);

    const totalSum = computed(() => {
      const itemsTotal = selectedItems.value.reduce((acc, item) => (acc + item.sale_price * item.qnt), 0);
      const delivery = useDelivery.value && deliveryCost.value ? deliveryCost.value : 0;
      return Math.max(0, itemsTotal + delivery - promoDiscount.value);
    });

    async function applyPromoHandler() {
      const code = (promoCodeInput.value || '').trim();
      if (!code) {
        toast.error("Укажите промокод");
        return;
      }
      if (!cartStore.selectedIds.length) {
        toast.error("Не выбраны товары");
        return;
      }
      cancelPromoRefresh();
      promoBusy.value = true;
      try {
        const applied = await ordersStore.applyPromo(code, cartStore.selectedIds);
        if (applied?.stale) {
          return;
        }
        if (!applied) {
          toast.error(ordersStore.error || "Промокод не применён");
          return;
        }
        toast.success("Промокод применён");
      } finally {
        promoBusy.value = false;
      }
    }

    function removePromoHandler() {
      cancelPromoRefresh();
      ordersStore.clearPromo();
      promoCodeInput.value = '';
    }

    // Состав корзины или количество могли измениться: пересчитываем скидку на сервере.
    watch(
      () => selectedItems.value.map(item => `${item.id}:${item.qnt}`).join(','),
      (current, previous, onCleanup) => {
        if (current === previous) {
          return;
        }
        const code = ordersStore.promo?.code || pendingPromoCode;
        if (!code) {
          return;
        }
        pendingPromoCode = '';
        ordersStore.clearPromo();
        if (!cartStore.selectedIds.length) {
          return;
        }
        pendingPromoCode = code;
        let cancelled = false;
        const refreshGeneration = ++promoRefreshGeneration;
        promoRefreshTimer = setTimeout(async () => {
          if (cancelled || refreshGeneration !== promoRefreshGeneration) return;
          promoBusy.value = true;
          try {
            const applied = await ordersStore.applyPromo(code, cartStore.selectedIds);
            if (applied?.stale || cancelled || refreshGeneration !== promoRefreshGeneration) {
              return;
            }
            pendingPromoCode = '';
            if (!applied) {
              toast.error(ordersStore.error || "Промокод больше не применим");
            }
          } finally {
            if (!cancelled && refreshGeneration === promoRefreshGeneration) {
              promoBusy.value = false;
            }
          }
        }, 300);
        onCleanup(() => {
          cancelled = true;
          clearTimeout(promoRefreshTimer);
          promoRefreshTimer = null;
        });
      }
    );

    const calcPoint = computed(() => {
      if(!props.selectedPoint) {
        return 'не задано'
      }
      return `${props.selectedPoint.city}, ${props.selectedPoint.address}  ${props.selectedPoint.code}`;
    })

    async function checkoutHandler() {

      if(busy.value){
         return;
       }
      const requirePoint = canUseDelivery.value && useDelivery.value;
      if (requirePoint && !props.selectedPoint) {
        toast.error("Не выбран пункт выдачи");
        return;
      }
      if(!checkEmail( ordersStore.checkoutData.email, false)) {
        toast.error("Неверно значение email");
        return;
      }
      if(!checkPhone(ordersStore.checkoutData.phone, false)) {
        toast.error("Неверно значение phone");
        return;
      }
      if(!(ordersStore.checkoutData.fio || '').trim()) {
        toast.error("Не задано ФИО");
        return;
      }
      try {
        busy.value = true;
        await ordersStore.makeOrder({
          ids: cartStore.selectedIds,
          comment: '',
          email: ordersStore.checkoutData.email,
          phone: ordersStore.checkoutData.phone,
          fio: ordersStore.checkoutData.fio,
          promo_code: appliedPromo.value?.code || undefined,
          use_delivery: canUseDelivery.value ? useDelivery.value : undefined,
          delivery_point: requirePoint && props.selectedPoint ? {
            address: props.selectedPoint.address,
            allowed_cod: props.selectedPoint.allowed_cod,
            city: props.selectedPoint.city,
            city_code: props.selectedPoint.city_code,
            name: props.selectedPoint.name,
            postal_code: props.selectedPoint.postal_code,
            region: props.selectedPoint.region,
            type: props.selectedPoint.type,
            weight_max: props.selectedPoint.weight_max,
            work_time: props.selectedPoint.work_time,
          } : {}
        })
        if (ordersStore.error) {
          toast.error(ordersStore.error);
          return
        }
        await settings.loadSetting();
        toast.success('Заказ сформирован')
        await payment.handlePayment();
      }finally {
        busy.value = false;
      }
    }

    return {
      promoCodeInput,
      promoBusy,
      appliedPromo,
      promoDiscount,
      applyPromoHandler,
      removePromoHandler,
      checkoutHandler,
      totalSum,
      selectedItems,
      calcPoint,
      canUseDelivery,
      useDelivery,
      deliveryCost,
      openImagePreview,
    }
  }
}
</script>


<style scoped lang="scss">
.checkoutPanel {
  background-color: var(--cd-card-bg);
  border-radius: 20px;
  padding: 24px;
  text-align: left;

  &__textLabel {
    padding: 10px 10px 10px;
    display: block;
  }

  &__textLabel--muted {
    display: block;
    color: var(--cd-text-muted);
    margin: 20px 0px;
  }

  &__products {
    width: 100%;
    height: 132px;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    margin-bottom: 10px;
  }

  &__image {
    height: 90%;
    width: 116px;
    object-fit: cover;
    border-radius: 20px;
    cursor: zoom-in;
  }

  &__options {
    margin-bottom: 10px;
  }

  &__promoRow {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__promoHint {
    margin-top: 8px;
    font-size: 14px;
    color: var(--cd-success);
  }

  &__deliveryToggle {
    display: flex;
    gap: 8px;
    margin-top: 6px;
  }

  &__toggleBtn {
    flex: 1;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid var(--cd-border);
    background-color: var(--cd-button-bg-color);
    color: var(--cd-text-muted);
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s, color 0.2s;

    &--active {
      background-color: var(--cd-success);
      color: #ffffff;
      border-color: var(--cd-success);
    }
  }

  &__costRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 0 2px;
  }

  &__costLabel {
    color: var(--cd-text-muted);
    font-size: 14px;
  }

  &__costValue {
    color: var(--cd-success);
    font-size: 16px;
    font-weight: 500;
  }

  &__footer {
    margin-top: 40px;
    display: flex;
    justify-content: space-between;
    text-align: left;
  }

  &__footerTotalSum {
    color: var(--cd-success);
    font-size: 20px;
  }
}
</style>
